/**
 * PRODUCT-154 — real color values, the single source of truth for both themes.
 *
 * This module holds the ONLY hard-coded hex/rgba color literals in the theming
 * layer. `color-theme.ts` re-exports every token as a `var(--wh-*)` string;
 * `ThemeVars.ts` emits those vars from the records below; `raw.ts` hands the
 * real values to consumers that can't use a `var()` string (canvas, WebGL,
 * mapbox GL paint props, JS color math).
 *
 * LIGHT is byte-equal to the constants `color-theme.ts` shipped before the
 * variable conversion — the Phase-2 regression test (`palette.test.ts`) locks
 * that so the var conversion is a zero-visual-diff refactor.
 *
 * DARK: neutrals invert (the gray ramp is reversed), chromatic ramps are
 * regenerated with the same `@ant-design/colors` generator `@ant-design/
 * compatible`'s `darkTheme` uses, and brand colors (navy/refa) are carried
 * unchanged as an inert first pass — dark is not reachable until the theme
 * engine lands (Phase 3), and the dark palette polish is a later pass.
 */
import { generate } from '@ant-design/colors'
import color from 'color-alpha'

// antd v4 seed ramps, 100 (lightest) → 1000 (darkest), copied byte-for-byte
// from the pre-conversion color-theme.ts.
const lightRamps: Record<string, string[]> = {
  gray: [
    '#FFFFFF',
    '#FAFAFA',
    '#F5F5F5',
    '#F0F0F0',
    '#D9D9D9',
    '#BFBFBF',
    '#8C8C8C',
    '#595959',
    '#262626',
    '#000000',
  ],
  red: [
    '#FFF1F0',
    '#FFCCC7',
    '#FFA39E',
    '#FF7875',
    '#FF4D4F',
    '#F5222D',
    '#CF1322',
    '#A8071A',
    '#820014',
    '#5C0011',
  ],
  orange: [
    '#FFF7E6',
    '#FFE7BA',
    '#FFD591',
    '#FFC069',
    '#FFA940',
    '#FA8C16',
    '#D46B08',
    '#AD4E00',
    '#873800',
    '#612500',
  ],
  yellow: [
    '#FEFFE6',
    '#FFFFB8',
    '#FFFB8F',
    '#FFF566',
    '#FFEC3D',
    '#FADB14',
    '#D4B106',
    '#AD8B00',
    '#876800',
    '#614700',
  ],
  green: [
    '#F6FFED',
    '#D9F7BE',
    '#B7EB8F',
    '#95DE64',
    '#73D13D',
    '#52C41A',
    '#389E0D',
    '#237804',
    '#135200',
    '#092B00',
  ],
  geekBlue: [
    '#F0F5FF',
    '#D6E4FF',
    '#ADC6FF',
    '#85A5FF',
    '#597EF7',
    '#2F54EB',
    '#1D39C4',
    '#10239E',
    '#061178',
    '#030852',
  ],
  magenta: [
    '#FFF0F6',
    '#FFD6E7',
    '#FFADD2',
    '#FF85C0',
    '#F759AB',
    '#EB2F96',
    '#C41D7F',
    '#9E1068',
    '#780650',
    '#520339',
  ],
  blue: [
    '#E6F7FF',
    '#BAE7FF',
    '#91D5FF',
    '#69C0FF',
    '#40A9FF',
    '#1890FF',
    '#096DD9',
    '#0050B3',
    '#003A8C',
    '#002766',
  ],
  volcano: [
    '#FFF2E8',
    '#FFD8BF',
    '#FFBB96',
    '#FF9C6E',
    '#FF7A45',
    '#FA541C',
    '#D4380D',
    '#AD2102',
    '#871400',
    '#610B00',
  ],
  gold: [
    '#FFFBE6',
    '#FFF1B8',
    '#FFE58F',
    '#FFD666',
    '#FFC53D',
    '#FAAD14',
    '#D48806',
    '#AD6800',
    '#874D00',
    '#613400',
  ],
  lime: [
    '#FCFFE6',
    '#F4FFBF',
    '#EAFF8F',
    '#D3F261',
    '#BAE637',
    '#A0D911',
    '#7CB305',
    '#5B8C00',
    '#3F6600',
    '#254000',
  ],
  cyan: [
    '#E6FFFB',
    '#B5F5EC',
    '#87E8DE',
    '#5CDBD3',
    '#36CFC9',
    '#13C2C2',
    '#08979C',
    '#006D75',
    '#00474F',
    '#002329',
  ],
  purple: [
    '#F9F0FF',
    '#EFDBFF',
    '#D3ADF7',
    '#B37FEB',
    '#9254DE',
    '#722ED1',
    '#531DAB',
    '#391085',
    '#22075E',
    '#120338',
  ],
}

const CHROMATIC = [
  'red',
  'orange',
  'yellow',
  'green',
  'geekBlue',
  'magenta',
  'blue',
  'volcano',
  'gold',
  'lime',
  'cyan',
  'purple',
] as const

// Brand / customer colors (navy = platform chrome, refa = Refa tenant).
const lightExtra: Record<string, string> = {
  navy900: '#111D2C',
  navy500: '#0F3F6B',
  navy200: '#ECEFF3',
  navy100: '#e8e9ea',
  refaDarkBlue: '#034F77',
  refaGray: '#E5E7E8',
  refaLightBlue: '#77A3B6',
  refaLightGreen: '#B8E5E1',
}

// Flatten hue arrays into gray100…purple1000 plus the brand extras.
function flatRamp(
  ramps: Record<string, string[]>,
  extra: Record<string, string>
): Record<string, string> {
  const flat: Record<string, string> = {}
  Object.entries(ramps).forEach(([name, shades]) => {
    shades.forEach((hex, i) => {
      flat[`${name}${(i + 1) * 100}`] = hex
    })
  })
  return { ...flat, ...extra }
}

// Semantic tokens, derived once and applied to each theme's ramp. Written to
// match the pre-conversion color-theme.ts token section exactly, so applying it
// to `lightRamp` reproduces today's values byte-for-byte (the `color()` alpha
// tokens included), and applying it to `darkRamp` yields sensible dark
// semantics for free (inverted neutrals flip fg/bg the right way).
function semanticTokens(r: Record<string, string>): Record<string, string> {
  return {
    // text
    textPrimary: r.gray900,
    // 0.65, not 0.50. At 0.50 this composites to #929292 on white — 3.11:1,
    // BELOW the 4.5:1 WCAG AA floor for normal text, and worse still on the
    // app's off-white surfaces (2.99:1 on #F0F0F0). 0.65 is the smallest round
    // alpha that clears AA on every light surface the token actually sits on:
    // 4.81:1 on white, 4.70:1 on #F9F9F9, 4.69:1 on #F5F7F9, 4.54:1 on
    // #F0F0F0. Dark is derived from the same expression and only improves
    // (4.86–5.14:1 → 7.19–8.04:1), because there the ramp is inverted and a
    // higher alpha means brighter text. Measured, not guessed — see
    // `__tests__/theme-tokens.test.ts`.
    textSecondary: color(r.gray900, 0.65),
    textEmpty: color(r.gray900, 0.3),
    textRevPrimary: r.gray100,
    textRevSecondary: color(r.gray100, 0.5),
    textLink: r.blue500,
    textDangerous: r.red500,
    // icon
    iconPrimary: color(r.gray900, 0.8),
    iconSecondary: color(r.gray900, 0.3),
    iconLink: r.blue500,
    iconDangerous: r.red500,
    // border
    bSection: r.gray300,
    bInputDefault: r.gray500,
    bInputHover: r.blue500,
    // background
    bgPage: r.gray100,
    bgSystem: r.navy200,
    bgInnerPrimary: r.gray100,
    bgInnerSecondary: r.gray300,
    bgTableHeader: r.gray200,
    bgMainTabs: r.gray300,
    bgCardHeader: r.navy900,
    bgOverlay: color(r.gray900, 0.6),
    bgHover: color(r.gray900, 0.05),
    bgTransp: 'transparent',
    bgNotificationSection: '#F5F7F9',
    // feedback
    fbDefault: r.navy900,
    fbWarning: r.gold500,
    fbCritical: r.red500,
    // stop status
    stopCompleted: r.green500,
    stopError: r.red500,
    stopNext: r.blue500,
    // priority
    pHigh: r.red500,
    pMedium: r.gold500,
    pLow: r.blue500,
    pNone: r.gray500,
    // divider
    divMain: color(r.gray900, 0.15),
    divSecondary: color(r.gray900, 0.05),
    divColor: '#ebeff3',
  }
}

const lightRamp = flatRamp(lightRamps, lightExtra)

// Dark ramp: reverse the neutral gray scale, regenerate each chromatic hue with
// the antd dark generator seeded from its 600-level primary, keep brand colors.
// The generated ramp is left AS-IS on purpose (PRO-509 polish is applied as
// targeted DARK-object overrides below, not by rewriting the ramp): gray600→
// gray1000 are the dark theme's TEXT/ICON foreground (textPrimary=gray900 etc.)
// and the mid consumers (tags/chips) depend on the reversed values.
const darkRamps: Record<string, string[]> = {
  gray: [...lightRamps.gray].reverse(),
}
CHROMATIC.forEach((name) => {
  darkRamps[name] = generate(lightRamps[name][5], {
    theme: 'dark',
    backgroundColor: '#1f1f1f',
  })
})
const darkRamp = flatRamp(darkRamps, { ...lightExtra })

// App-shell surface tokens (PRODUCT-154 phase 4c). LIGHT values are byte-
// identical to the pre-sweep shell literals (#FFFFFF header/sider, #ECEFF3 app
// bg = navy200/bgSystem); DARK gives the shell a coherent hierarchy that mirrors
// light — elevated chrome (header/sider) sits lighter than the deeper app bg,
// just as white chrome sits over the grey app bg today.
export const LIGHT: Record<string, string> = {
  ...lightRamp,
  ...semanticTokens(lightRamp),
  bgHeader: '#FFFFFF',
  bgSider: '#FFFFFF',
  // Header foreground pair: light value = the navy900 the header icons used
  // before the sweep (byte-identical); dark value must stay legible on the
  // dark bgHeader — brand navy900 is NOT dark-ramped, so it can't be reused.
  iconHeader: '#111D2C',
  // Subtle nested fill sitting just below a card surface (audit diff rows,
  // recessed inner panels). LIGHT = the #F9F9F9 those surfaces hard-coded
  // (byte-identical); DARK lifts one step off the card (gray100=#000) so the
  // fill still reads as recessed-but-elevated, mirroring the light hierarchy.
  bgInnerSubtle: '#F9F9F9',
}
export const DARK: Record<string, string> = {
  ...darkRamp,
  ...semanticTokens(darkRamp),

  // ── PRO-509 dark surface hierarchy (targeted overrides; ramp generation,
  // gray600–1000, chromatic ramps, tags/chips/hover, textPrimary/textSecondary
  // all left as generated). Root cause fixed: today bgSystem AND bgInnerPrimary
  // both resolve to gray100=#000000 — app bg and cards are the same pure black.
  // Target = a desaturated near-black elevation ladder (Linear/Slack-grade). ──

  // surface-end neutral dark values → the elevation ladder. These four indices
  // are used overwhelmingly as SURFACES/borders in dark (55/39/42/5 background
  // sites) with ≤1 foreground use each (re-routed to text* below). gray500 is
  // NOT overridden on purpose (§5 caution): it has 0 surface uses but 195
  // FOREGROUND (muted-text) uses — overriding it to a border value would render
  // that text near-invisible on dark. Its 23 border uses keep the ramp value;
  // antd's own colorBorder still supplies dark borders to antd components.
  gray100: '#0E0F11', // base plane / app-bg counterpart of #FFFFFF-light
  gray200: '#17181A', // card    (sweep #fafafa→)
  gray300: '#1E1F22', // elevated (sweep #f5f5f5→)
  gray400: '#202225', // chrome-adjacent (sweep #f0f0f0→)

  // semantic SURFACES (pinned: several share a gray index that now differs by role)
  bgSystem: '#0E0F11', // app background (L0, deepest)
  bgPage: '#0E0F11',
  bgInnerPrimary: '#17181A', // card / content (L1)
  bgInnerSecondary: '#1E1F22', // nested / elevated (L2)
  bgInnerSubtle: '#1E1F22', // sweep #F9F9F9 fill → one step off the card
  bgTableHeader: '#17181A',
  bgMainTabs: '#1E1F22',
  bgHeader: '#202225', // chrome (L3)
  bgSider: '#202225',
  bgCardHeader: '#141A24', // dark-navy accent, aligned to the ladder
  bgNotificationSection: '#1E1F22', // was hardcoded '#F5F7F9' LIGHT literal
  // bgHover stays as generated (already color(gray900, 0.05) = white @ 5%).

  // semantic BORDERS / dividers (subtle; must NOT inherit a surface value)
  bSection: '#2B2D31',
  bInputDefault: '#34373C',
  bInputHover: '#4A4E55',
  divColor: '#2B2D31', // was hardcoded '#ebeff3' LIGHT literal

  // TEXT: text-end ramp kept as-is (textPrimary=gray900≈#FAFAFA AAA;
  // textSecondary@0.65 = AAA here — see the note on the token itself, which
  // was raised from 0.5 for AA in LIGHT). ONE change: disabled/empty at 0.3 alpha FAILS AA
  // (2.6:1) on these darker surfaces — bump to 0.50 (≈4.9:1, AA).
  textEmpty: color(darkRamp.gray900, 0.5),

  // ACCENT text lifted for AA on the darker surfaces (success/warning pass as-is)
  textLink: '#5AA2FF',
  textDangerous: '#FF7875',

  iconHeader: darkRamp.gray900,
}

export type PaletteToken = keyof typeof LIGHT
export const PALETTE_TOKENS = Object.keys(LIGHT) as PaletteToken[]

/** camelCase/number token name → CSS custom-property name (`gray100` → `gray-100`). */
export function tokenToCssVar(name: string): string {
  return `--wh-${name
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([a-zA-Z])([0-9])/g, '$1-$2')
    .toLowerCase()}`
}
